import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type ContentType = 'review_comment' | 'activity_comment' | 'message';
export type ReportStatus = 'pending' | 'reviewed';

export interface ContentReport {
  id: string;
  content_type: ContentType;
  content_id: string;
  report_category: string;
  additional_details: string | null;
  status: ReportStatus;
  created_at: string;
  reviewed_at: string | null;
  review_notes: string | null;
  reporter: {
    username: string;
    name: string;
    image: string | null;
  };
  reviewer: {
    username: string;
    name: string;
  } | null;
  content?: any;
}

interface ModerationFilters {
  status?: ReportStatus;
  contentType?: ContentType;
  startDate?: string;
  endDate?: string;
}

export function useContentModeration() {
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, total: 0, today: 0 });
  const { toast } = useToast();

  const fetchReports = async (filters: ModerationFilters = {}) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('content_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.contentType) {
        query = query.eq('content_type', filters.contentType);
      }
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch profiles and content for each report
      const reportsWithData = await Promise.all(
        (data || []).map(async (report) => {
          // Fetch reporter profile
          const { data: reporterProfile } = await supabase
            .from('profiles')
            .select('username, name, image')
            .eq('id', report.reporter_id)
            .single();

          // Fetch reviewer profile if exists
          let reviewerProfile = null;
          if (report.reviewed_by) {
            const { data: reviewer } = await supabase
              .from('profiles')
              .select('username, name')
              .eq('id', report.reviewed_by)
              .single();
            reviewerProfile = reviewer;
          }

          // Fetch content
          const content = await fetchContent(report.content_type as ContentType, report.content_id);

          return {
            ...report,
            reporter: reporterProfile || { username: 'unknown', name: 'Unknown', image: null },
            reviewer: reviewerProfile,
            content,
          } as ContentReport;
        })
      );

      setReports(reportsWithData);

      // Fetch stats
      const { data: allReports } = await supabase
        .from('content_reports')
        .select('id, status, created_at');

      if (allReports) {
        const today = new Date().toISOString().split('T')[0];
        setStats({
          pending: allReports.filter(r => r.status === 'pending').length,
          total: allReports.length,
          today: allReports.filter(r => r.created_at.startsWith(today)).length,
        });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch reports',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContent = async (contentType: ContentType, contentId: string) => {
    try {
      switch (contentType) {
        case 'review_comment': {
          const { data } = await supabase
            .from('review_comments')
            .select('*, author:profiles!user_id(username, name, image)')
            .eq('id', contentId)
            .maybeSingle();
          return data;
        }
        case 'activity_comment': {
          const { data } = await supabase
            .from('activity_comments')
            .select('*, author:profiles!user_id(username, name, image)')
            .eq('id', contentId)
            .maybeSingle();
          return data;
        }
        case 'message': {
          const { data } = await supabase
            .from('messages')
            .select('*, author:profiles!user_id(username, name, image)')
            .eq('id', contentId)
            .maybeSingle();
          return data;
        }
        default:
          return null;
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      return null;
    }
  };

  const updateReportStatus = async (
    reportId: string,
    status: ReportStatus,
    notes: string
  ) => {
    try {
      const { error } = await supabase
        .from('content_reports')
        .update({
          status,
          review_notes: notes,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', reportId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Report updated successfully',
      });

      return true;
    } catch (error) {
      console.error('Error updating report:', error);
      toast({
        title: 'Error',
        description: 'Failed to update report',
        variant: 'destructive',
      });
      return false;
    }
  };

  const dismissReport = async (reportId: string, notes: string) => {
    return updateReportStatus(reportId, 'reviewed', notes);
  };

  const markAsActioned = async (reportId: string, notes: string) => {
    return updateReportStatus(reportId, 'reviewed', notes);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  return {
    reports,
    loading,
    stats,
    fetchReports,
    dismissReport,
    markAsActioned,
    updateReportStatus,
  };
}
